import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { SessaoProvider } from './lib/sessao';
import App from './App';
// A fonte do Grafite, servida pelo próprio sistema (a CSP é `font-src 'self'`):
// o Vite copia os .woff2 para /assets/, que o service worker já guarda para o
// balcão sem internet. Antes do index.css, que é quem a usa.
import '@fontsource-variable/geist';
import '@fontsource-variable/geist-mono';
import './index.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // O balcão consulta a mesma peça várias vezes em sequência; 30s de cache
      // corta ida ao servidor sem risco de mostrar estoque velho numa venda.
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
      // 'offlineFirst' e não o padrão 'online': com 'online' o TanStack nem
      // CHAMA a consulta quando o navegador se diz offline — e aí o service
      // worker nunca é acionado, logo a cópia guardada da busca não chega à
      // tela. Aqui a primeira tentativa sempre sai; quem responde com o que
      // tem guardado é o sw.js. Sem rede e sem cópia, falha uma vez e para
      // (não fica retentando em pânico com o 4G caído).
      networkMode: 'offlineFirst',
    },
    mutations: {
      // 'always' e não o padrão 'online': com 'online' uma venda disparada sem
      // internet fica PAUSADA — botão girando, nenhum erro — e o TanStack a
      // executa sozinho quando a conexão volta. Isso é uma fila de vendas
      // acidental, que é exatamente o que não queremos: o vendedor conclui
      // que falhou, anota no papel e refaz, e meia hora depois a primeira
      // dispara sozinha. Com 'always' a escrita falha na hora, com recado.
      networkMode: 'always',
    },
  },
});

// PWA de verdade começa aqui. Só em produção: em desenvolvimento um service
// worker guardando os módulos do Vite faz o `npm run dev` servir código velho
// e custa uma tarde de depuração de bug que não existe.
import { vigiarAtualizacoes } from './lib/atualizacao';

if ('serviceWorker' in navigator) {
  if (import.meta.env.PROD) {
    // Depois do load: registrar antes disso disputa banda com o primeiro
    // desenho da tela, justamente no celular ruim em que isso dói.
    window.addEventListener('load', () => {
      navigator.serviceWorker.register('/sw.js').then((registro) => {
        // "Tem versão nova" sem ninguém apertar Ctrl+Shift+R (22/08/2026).
        // O balcão deixa a aba aberta o dia inteiro; sem isto, quem não
        // recarrega roda o código de ontem.
        vigiarAtualizacoes(registro, {
          temControlador: Boolean(navigator.serviceWorker.controller),
        });
      }).catch((erro) => {
        console.warn('Service worker não registrado — o app segue, sem offline.', erro);
      });
    });
  } else {
    // Quem já rodou o build e abriu o preview fica com um SW registrado no
    // localhost; sem isto ele continua interceptando o dev server.
    navigator.serviceWorker.getRegistrations?.()
      .then((registros) => registros.forEach((r) => r.unregister()))
      .catch(() => {});
  }
}

const raiz = createRoot(document.getElementById('root')!);

// A VITRINE do Meu dinheiro (src/pessoal/vitrine/): as telas com a pessoa de
// exemplo, para ver e fotografar sem login, sem banco e sem a trava. Só em
// `npm run dev`, e NO LUGAR do sistema — o dev sem VITE_SUPABASE_URL mostraria
// "Sistema não configurado". O import é dinâmico e fica atrás de
// import.meta.env.DEV: no build de produção o Vite troca DEV por false, o ramo
// morre e nem a vitrine nem o exemplo entram no pacote.
if (import.meta.env.DEV && window.location.pathname.startsWith('/vitrine/meu-dinheiro')) {
  void import('./pessoal/vitrine/Vitrine').then(({ default: Vitrine }) => {
    raiz.render(
      <StrictMode>
        <QueryClientProvider client={queryClient}>
          <Vitrine />
        </QueryClientProvider>
      </StrictMode>,
    );
  });
} else {
  raiz.render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <SessaoProvider>
            <App />
          </SessaoProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </StrictMode>,
  );
}
